import type { RouteLocationRaw } from 'vue-router';
import { HOME_PATH } from '@/router/paths';

/**
 * Mappe le paramètre `?to=` d'un deep link `/go` vers une route Vue Router.
 * Source de vérité unique, partagée par le handler natif (App.vue) et la
 * garde de route web `/go`. Toute valeur absente ou inconnue tombe sur
 * l'accueil : un clic depuis un email ne doit jamais aboutir à une erreur.
 *
 * Chaque cible existe parce qu'un CTA d'email la promet. Ajouter une clé ici
 * sans que le libellé du bouton corresponde, ou l'inverse, recrée exactement
 * le défaut qu'on corrige : un bouton « Activer le rappel du soir » qui
 * atterrit sur l'accueil.
 */
const GO_TARGETS: Record<string, string> = {
  // L'accueil a repris le rôle de l'ancien tableau de bord. La clé reste : de
  // vieux emails la portent encore.
  dashboard: HOME_PATH,
  // Page de gestion des tâches, déclinaison rattachée à l'onglet Tâches : le
  // retour d'onglet reste cohérent après un atterrissage depuis un email.
  tasks: '/tabs/tasks/manage',
  rewards: '/tabs/rewards',
  reminders: '/tabs/settings/notifications',
  // La page de pitch a disparu avec le modèle par fonctionnalité. Les emails
  // qui portent encore cette clé atterrissent sur la carte d'abonnement, seule
  // surface qui parle d'Harmonia+ hors paywall.
  premium: '/tabs/settings',
  // Directement l'écran de saisie du code, jamais /join ni /join/welcome, qui
  // remettent le store joinFlow à zéro et effaceraient le code pré-rempli.
  join: '/join/code',
  // CTA de l'email dominical (send-weekly-recap). Il ouvrait le carrousel posé
  // en tête de l'ancien tableau de bord ; le bilan n'a plus d'entrée manuelle,
  // seulement la célébration poussée par la file de notifications. Le lien pose
  // donc sur l'accueil en attendant de lui rendre une porte. Clé tout en
  // minuscules : le lookup fait to.toLowerCase().
  weeklyrecap: HOME_PATH,

  // ── Onboarding piloté par Mailtrap (3.0.3) ─────────────────────────────
  // Les CTA vivent dans les automations Mailtrap. Référence des cibles :
  // docs/email-deeplinks.md.
  //
  // « Voir les tâches du jour » (bienvenue, B1, C1) : l'accueil, où le parent
  // voit la journée de la famille et valide depuis le même écran.
  today: HOME_PATH,
  // « Terminer la configuration » (A1, A2) : le parent a un compte mais pas
  // encore de foyer. Viser l'accueil suffit, la garde de route fait le reste :
  // un onboarding en cours reprend à son étape (règle 1), un compte sans état
  // d'onboarding atterrit sur /welcome?logged-in-no-household (règle 4), et un
  // parent qui a créé son foyer entre-temps arrive simplement sur l'accueil.
  onboarding: HOME_PATH,
  // « Alléger ma liste » (B2) et « Repartir avec une tâche » (C2) utilisent
  // `tasks`, déjà connue des versions antérieures : une clé dédiée tomberait
  // sur l'accueil dans une app pas encore à jour.
};

const DEFAULT_TARGET = HOME_PATH;

/**
 * Les codes d'invitation font 6 chiffres. Les 8 caractères alphanumériques
 * restent acceptés : une application non mise à jour continue d'en produire,
 * et son code doit rester ouvrable. À retirer quand le parc aura tourné.
 */
const INVITATION_CODE_LENGTHS = [6, 8];

/**
 * Ne laisse passer qu'un code exactement conforme. Un lien tronqué par un
 * client mail, ou une valeur bricolée à la main, doit poser l'utilisateur sur
 * l'écran de saisie vide plutôt que d'y injecter un code voué à échouer : le
 * code figure en clair dans le corps de l'email, il peut le recopier.
 */
export function sanitizeInvitationCode(raw: string | null | undefined): string {
  const cleaned = (raw ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return INVITATION_CODE_LENGTHS.includes(cleaned.length) ? cleaned : '';
}

export interface GoParams {
  /** Code d'invitation transporté par le CTA de l'email `invite_parent`. */
  code?: string | null;
}

export function resolveGoTarget(
  to: string | null | undefined,
  params?: GoParams,
): RouteLocationRaw {
  const path = to ? (GO_TARGETS[to.toLowerCase()] ?? DEFAULT_TARGET) : DEFAULT_TARGET;

  if (path === GO_TARGETS.join) {
    const code = sanitizeInvitationCode(params?.code);
    if (code) return { path, query: { code } };
  }

  return { path };
}
